import React, { useState } from 'react';
import {
  Card,
  Typography,
  Grid,
  Box,
  Stack,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Layers,
  Users,
  Radio,
  Inbox,
  AlertTriangle,
  ChevronRight,
  Clock,
  Search,
} from 'lucide-react';
import { useRabbitmqLive } from '../hooks/useRabbitmqLive.js';
import NodeHealthCard from './NodeHealthCard.jsx';

export function RabbitmqDashboard({
  onSelectQueueForInspection,
  selectedQueueName,
}) {
  const { snapshot, connected, isDegraded } = useRabbitmqLive();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWarnOnly, setFilterWarnOnly] = useState(false);
  const [lastKnownNodes, setLastKnownNodes] = useState([]);

  React.useEffect(() => {
    if (snapshot?.nodes && snapshot.nodes.length > 0) {
      setLastKnownNodes(snapshot.nodes);
    }
  }, [snapshot?.nodes]);

  const displayNodes =
    snapshot?.nodes && snapshot.nodes.length > 0 ? snapshot.nodes : lastKnownNodes;

  if (!snapshot) {
    return (
      <Card sx={{ p: 6, textAlign: 'center', minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={36} sx={{ color: '#006951', mb: 2 }} />
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          Connecting to RabbitMQ feed...
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Establishing real-time connection to backend gateway (/rabbitmq)
        </Typography>
      </Card>
    );
  }

  const lastUpdateTime = snapshot.timestamp
    ? new Date(snapshot.timestamp).toLocaleTimeString()
    : 'Just now';

  const queues = snapshot.queues || [];
  const warnQueuesCount = queues.filter((q) => q.consumers === 0 && q.messages > 0).length;

  const filteredQueues = queues.filter((q) => {
    const matchesSearch = q.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesWarn = filterWarnOnly ? q.consumers === 0 && q.messages > 0 : true;
    return matchesSearch && matchesWarn;
  });

  const isSimulated = Boolean(snapshot.is_simulated || snapshot.overview?.is_simulated);

  return (
    <Stack spacing={3}>
      {/* 1. Live Status Header */}
      <Card sx={{ px: 2.5, py: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: isSimulated ? '#f59e0b' : connected && !isDegraded ? '#10b981' : '#f59e0b',
                boxShadow: isSimulated ? '0 0 8px rgba(245,158,11,0.8)' : '0 0 8px rgba(16,185,129,0.8)',
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 700, color: isSimulated ? '#d97706' : '#059669' }}>
              {isSimulated
                ? 'Demo Mode (Simulated Traffic)'
                : connected && !isDegraded
                ? 'Live (Socket.IO)'
                : 'Reconnecting...'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Clock style={{ width: 12, height: 12 }} /> updated {lastUpdateTime}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={
                <span>
                  Cluster: <strong style={{ color: '#006951' }}>{snapshot.overview?.clusterName || 'Broker'}</strong>
                </span>
              }
              size="small"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: '#f1f5f9' }}
            />
            <Chip
              label={`RabbitMQ ${snapshot.overview?.rabbitmqVersion ? `v${snapshot.overview.rabbitmqVersion}` : 'Live'}`}
              size="small"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: '#f1f5f9' }}
            />
          </Stack>
        </Stack>
      </Card>

      {/* 2. Degraded Downtime Banner */}
      {(snapshot.error || isDegraded) && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <strong>RabbitMQ unreachable</strong> — Broker down or unreachable. Last successful update at {lastUpdateTime}.
        </Alert>
      )}

      {/* 3. Actionable Alert for zero consumers */}
      {warnQueuesCount > 0 && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => setFilterWarnOnly(!filterWarnOnly)}
              sx={{ fontWeight: 700, fontSize: '0.75rem' }}
            >
              {filterWarnOnly ? 'Show All' : 'Filter Warn Queues'}
            </Button>
          }
        >
          <strong>Action Needed:</strong> {warnQueuesCount} queue{warnQueuesCount > 1 ? 's are' : ' is'} holding messages with <strong>0 attached consumers</strong>.
        </Alert>
      )}

      {/* 4. KPI Cards - With Light Tinted Backgrounds and Matching Colors */}
      {(() => {
        const kpiCards = [
          {
            id: 'total-messages',
            title: 'Total Messages',
            value: snapshot.totalMessages.toLocaleString(),
            subtitle: 'Ready + Unacknowledged',
            icon: <Inbox style={{ width: 18, height: 18 }} />,
            accentColor: '#64748b',
            cardBg: '#f8fafc',
            cardBorder: '#cbd5e1',
            titleColor: '#475569',
            valueColor: '#1e293b',
            subtitleColor: '#64748b',
            bgLight: 'rgba(100, 116, 139, 0.16)',
            bgLightHover: 'rgba(100, 116, 139, 0.26)',
            shadowColor: 'rgba(100, 116, 139, 0.22)',
          },
          {
            id: 'total-queues',
            title: 'Total Queues',
            value: snapshot.totalQueues.toLocaleString(),
            subtitle: 'Active on vhost',
            icon: <Layers style={{ width: 18, height: 18 }} />,
            accentColor: '#0284c7',
            cardBg: '#f0f9ff',
            cardBorder: '#bae6fd',
            titleColor: '#075985',
            valueColor: '#0369a1',
            subtitleColor: '#0284c7',
            bgLight: 'rgba(2, 132, 199, 0.16)',
            bgLightHover: 'rgba(2, 132, 199, 0.26)',
            shadowColor: 'rgba(2, 132, 199, 0.22)',
          },
          {
            id: 'total-consumers',
            title: 'Total Consumers',
            value: snapshot.totalConsumers.toLocaleString(),
            subtitle: 'Attached workers',
            icon: <Users style={{ width: 18, height: 18 }} />,
            accentColor: '#0d9488',
            cardBg: '#f0fdfa',
            cardBorder: '#99f6e4',
            titleColor: '#115e59',
            valueColor: '#0f766e',
            subtitleColor: '#0d9488',
            bgLight: 'rgba(13, 148, 136, 0.16)',
            bgLightHover: 'rgba(13, 148, 136, 0.26)',
            shadowColor: 'rgba(13, 148, 136, 0.22)',
          },
          {
            id: 'connections',
            title: 'Connections',
            value: snapshot.connectionCount.toLocaleString(),
            subtitle: 'Open AMQP client sockets',
            icon: <Radio style={{ width: 18, height: 18 }} />,
            accentColor: '#7c3aed',
            cardBg: '#faf5ff',
            cardBorder: '#e9d5ff',
            titleColor: '#581c87',
            valueColor: '#6b21a8',
            subtitleColor: '#7e22ce',
            bgLight: 'rgba(124, 58, 237, 0.16)',
            bgLightHover: 'rgba(124, 58, 237, 0.26)',
            shadowColor: 'rgba(124, 58, 237, 0.22)',
          },
        ];

        return (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2.5,
              width: '100%',
            }}
          >
            {kpiCards.map((kpi) => (
              <Card
                key={kpi.id}
                sx={{
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  border: '1.5px solid',
                  borderColor: kpi.cardBorder,
                  bgcolor: kpi.cardBg,
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
                  transition: 'all 0.24s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    bgcolor: kpi.accentColor,
                    opacity: 0.9,
                    transition: 'opacity 0.24s ease, height 0.24s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: kpi.accentColor,
                    boxShadow: `0 14px 28px -4px ${kpi.shadowColor}, 0 6px 12px -2px rgba(0, 0, 0, 0.06)`,
                    '&::before': {
                      height: '5px',
                    },
                    '& .kpi-icon-box': {
                      transform: 'scale(1.15)',
                      bgcolor: kpi.bgLightHover,
                    },
                  },
                }}
              >
                <div>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography
                      className="kpi-title"
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: kpi.titleColor,
                        fontSize: '0.72rem',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {kpi.title}
                    </Typography>
                    <Box
                      className="kpi-icon-box"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        bgcolor: kpi.bgLight,
                        color: kpi.accentColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {kpi.icon}
                    </Box>
                  </Stack>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      fontFamily: 'monospace',
                      color: kpi.valueColor,
                      letterSpacing: '-0.02em',
                      mt: 0.5,
                    }}
                  >
                    {kpi.value}
                  </Typography>
                </div>
                <Stack direction="row" alignItems="center" spacing={0.8} mt={1.8}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: kpi.accentColor,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: kpi.subtitleColor, fontSize: '0.74rem', fontWeight: 600 }}>
                    {kpi.subtitle}
                  </Typography>
                </Stack>
              </Card>
            ))}
          </Box>
        );
      })()}

      {/* 5. Broker Nodes Health */}
      {displayNodes.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', mb: 1.5 }}>
            Broker Node Health
          </Typography>
          <NodeHealthCard nodes={displayNodes} />
        </Box>
      )}

      {/* 6. Main Queues Table */}
      <Card>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Cluster Queues
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Live queue metrics refreshed via WebSocket push
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="Search queues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ width: 14, height: 14, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: 2, fontSize: '0.75rem', width: { xs: '100%', sm: 240 } },
              }}
            />
          </Stack>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Queue</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>State</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Messages</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Ready</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Unacked</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Consumers</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Publish/s</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Deliver/s</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQueues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No queues match your filter criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredQueues.map((q) => {
                  const isWarn = q.consumers === 0 && q.messages > 0;
                  const isSelected = selectedQueueName === q.name;

                  return (
                    <TableRow
                      key={q.name}
                      hover
                      onClick={() => onSelectQueueForInspection && onSelectQueueForInspection(q.name)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isWarn ? 'rgba(245, 158, 11, 0.08)' : isSelected ? 'rgba(0, 105, 81, 0.06)' : 'inherit',
                        borderLeft: isWarn ? '4px solid #f59e0b' : isSelected ? '4px solid #006951' : 'none',
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: isSelected ? 600 : 500 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span style={{ color: isSelected ? '#006951' : 'inherit' }}>{q.name}</span>
                          {isWarn && (
                            <Chip
                              icon={<AlertTriangle style={{ width: 12, height: 12, color: '#d97706' }} />}
                              label="No Consumers"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: 'rgba(245, 158, 11, 0.15)',
                                color: '#d97706',
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={q.state}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            bgcolor: q.state === 'running' ? 'rgba(16, 185, 129, 0.1)' : '#f1f5f9',
                            color: q.state === 'running' ? '#059669' : '#64748b',
                          }}
                        />
                      </TableCell>

                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                        {q.messages.toLocaleString()}
                      </TableCell>

                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#059669', fontWeight: 600 }}>
                        {q.ready.toLocaleString()}
                      </TableCell>

                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#d97706', fontWeight: 600 }}>
                        {q.unacked.toLocaleString()}
                      </TableCell>

                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: isWarn ? '#dc2626' : 'inherit', fontWeight: isWarn ? 800 : 500 }}>
                        {q.consumers}
                      </TableCell>

                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {q.publishRate.toFixed(1)}/s
                      </TableCell>

                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {q.deliverRate.toFixed(1)}/s
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          endIcon={<ChevronRight style={{ width: 12, height: 12 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onSelectQueueForInspection) {
                              onSelectQueueForInspection(q.name);
                            }
                          }}
                          sx={{
                            fontSize: '0.7rem',
                            py: 0.3,
                            px: 1,
                            bgcolor: isSelected ? '#006951' : 'transparent',
                            borderColor: isSelected ? '#006951' : 'divider',
                            color: isSelected ? '#fff' : 'text.primary',
                            '&:hover': {
                              bgcolor: isSelected ? '#005441' : 'rgba(0, 105, 81, 0.05)',
                              borderColor: '#006951',
                            },
                          }}
                        >
                          {isSelected ? 'Inspecting' : 'Inspect'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
}
